import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { goTo } from "@client/lib/navigation";
import { TargetSelector } from "@client/selectors";
import { type EntityRef, Status } from "@client/types";
import { type AppState } from "@client/types/AppState";
import { type AnyAction, type Dispatch } from "redux";
import { initAction, redirect } from "../actions";
import { processEnroll } from "../OptInFormV2/lib";
import api from "./api";
import { getCheckoutOrderId, mapBilling, mapShipping, shouldAdjustOrder } from "./lib";
import { localStateSelector, makeOrderInfoTriggerParamsSelector } from "./selectors";
import { type Checkout } from "./types";

export enum Actions {
  ClearFailureReason = "MODULE_CHECKOUT_CLEAR_FAILURE_REASON",

  InitBegin = "MODULE_CHECKOUT_INIT_BEGIN",
  InitFailure = "MODULE_CHECKOUT_INIT_FAILURE",
  InitSuccess = "MODULE_CHECKOUT_INIT_SUCCESS",

  AdjustOrderBegin = "MODULE_CHECKOUT_ADJUST_ORDER_BEGIN",
  AdjustOrderFailure = "MODULE_CHECKOUT_ADJUST_ORDER_FAILURE",
  AdjustOrderSuccess = "MODULE_CHECKOUT_ADJUST_ORDER_SUCCESS",

  PlaceOrderBegin = "MODULE_CHECKOUT_PLACE_ORDER_BEGIN",
  PlaceOrderFailure = "MODULE_CHECKOUT_PLACE_ORDER_FAILURE",
  PlaceOrderSuccess = "MODULE_CHECKOUT_PLACE_ORDER_SUCCESS",

  SetShouldAdjustOrder = "MODULE_CHECKOUT_SET_SHOULD_ADJUST_ORDER",

  SetOrder = "MODULE_CHECKOUT_SET_ORDER",
  SetVariantId = "MODULE_CHECKOUT_SET_VARIANT_ID"
}

export const clearFailureReason = (ref: EntityRef) => ({
  type: Actions.ClearFailureReason,
  data: ref
});

export type AdjustOrderInfoBeginActionData = EntityRef & Checkout.Api.AdjustOrderInfo;
export type AdjustOrderInfoSuccessActionData = Checkout.Api.AdjustOrderInfoResponse;
export const _adjustOrder = apiAction<AppState, AdjustOrderInfoBeginActionData, Checkout.Api.AdjustOrderInfoResponse>({
  types: {
    begin: Actions.AdjustOrderBegin,
    failure: Actions.AdjustOrderFailure,
    success: [
      Actions.AdjustOrderSuccess,
      ({ dispatch, getState, params }) => {
        const state = getState();
        const localState = localStateSelector(state, params);

        if (localState.shouldAdjust) {
          dispatch(
            _adjustOrder({
              id: params.id,
              source: params.source,
              ...localState.adjustOrderInfo,
              ...localState.order!
            })
          );
        }
      }
    ]
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, params: AdjustOrderInfoBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);
    const order = localState.order!;

    const args: Checkout.Api.AdjustOrderInfoRequest = {
      portalId: state.portal.id,
      ...params,
      ...mapBilling(order, localState.cart.requiresShipping),
      ...mapShipping(order)
    };

    const triggerParams = makeOrderInfoTriggerParamsSelector()(state, params);

    return api.adjustOrderInfo(apiInfo, { ...args, ...triggerParams });
  }
});

export type AdjustOrderInfoArgs = EntityRef;
export const adjustOrder = (args: AdjustOrderInfoArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.shouldAdjust && localState.status !== "AdjustingOrderInfo") {
    dispatch(
      _adjustOrder({
        id: args.id,
        source: args.source,
        ...localState.adjustOrderInfo,
        ...localState.order!
      })
    );
  }
};

export type PrepareGetOrderInfoArgs = EntityRef & Checkout.AdjustOrderInfo;
export type SetShouldAdjustOrderActionData = EntityRef &
  Checkout.AdjustOrderInfo & {
    coupons: string[] | null;
    shouldAdjust: boolean;
  };
export const prepareGetOrderInfo =
  ({ id, ...args }: PrepareGetOrderInfoArgs, coupons: string[] | null) =>
  (dispatch: Dispatch<any>, getState: () => AppState) => {
    const state = getState();
    const localState = localStateSelector(state, { id });

    const shouldAdjust = localState.shouldAdjust || shouldAdjustOrder(localState.adjustOrderInfo, args);

    dispatch({
      type: Actions.SetShouldAdjustOrder,
      data: <SetShouldAdjustOrderActionData>{
        ...args,
        id,
        coupons: coupons ?? localState.cart.coupons,
        shouldAdjust
      }
    });

    if (localState.status !== "AdjustingOrderInfo") {
      // if coupons are specified - trigger immediately
      if (coupons) {
        dispatch(
          _adjustOrder({
            id,
            ...args,
            coupons: coupons ?? localState.cart.coupons,
            ...localState.order!
          })
        );
      }
    }
  };

export type PlaceOrderBeginActionData = EntityRef & Checkout.Api.PlaceOrderRequest;
export type PlaceOrderSuccessActionData = Checkout.Api.PlaceOrderResponse;
const _placeOrder = apiAction<AppState, PlaceOrderBeginActionData, PlaceOrderSuccessActionData>({
  types: {
    begin: Actions.PlaceOrderBegin,
    failure: Actions.PlaceOrderFailure,
    success: [
      async ({ dispatch, getState, params, res }) => {
        if (res.failureReason) return;

        if (processEnroll(getState(), res.enroll)) return;

        if (res.redirectUrl) {
          goTo(res.redirectUrl);
        } else {
          const localState = localStateSelector(getState(), params);

          await dispatch(
            redirect({
              id: params.id,
              source: params.source,
              target: TargetSelector.Entity,
              fallbackUrl: localState.confirmUrl,
              orderId: res.orderId
            })
          );
        }
      },
      Actions.PlaceOrderSuccess
    ]
  },
  action: async (state: AppState, dispatch: Dispatch<AnyAction>, args: PlaceOrderBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const localState = localStateSelector(state, args);

    return api.placeOrder(apiInfo, {
      ...mapBilling(args, localState.cart.requiresShipping),
      ...args
    });
  }
});

export const placeOrder =
  (ref: EntityRef, data: Checkout.Order) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    const state = getState();
    const localState = localStateSelector(state, { id: ref.id });

    if (localState.status === Status.Ready) {
      dispatch(
        _placeOrder({
          ...ref,
          portalId: state.portal.id,
          ...data,
          shippingRuleId:
            data.shippingRuleId ?? (localState.cart.shippingRules.length ? localState.cart.shippingRules[0].id : null)
        })
      );
    }
  };

export type InitBeginActionData = EntityRef & {};
export type InitSuccessActionData = Checkout.Api.InitResponse;
export const _init = initAction<AppState, InitBeginActionData, Checkout.Api.InitResponse>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.init(apiInfo, {
      portalId: state.portal.id,
      pageVariantId: state.page.variantId!,
      moduleId: args.id,
      orderId: getCheckoutOrderId()
    });
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type SetOrderActionData = EntityRef & {
  order: Checkout.Order;
};
export const setOrder = (ref: EntityRef, order: Checkout.Order) => ({
  type: Actions.SetOrder,
  data: {
    ...ref,
    order
  }
});

export type SetVariantIdActionData = EntityRef & {
  variantId: string;
};
export const setVariantId =
  (ref: EntityRef, variantId: string | null) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    const localState = localStateSelector(getState(), ref);

    if (localState.productVariantId !== variantId) {
      dispatch({
        type: Actions.SetVariantId,
        data: {
          ...ref,
          variantId
        }
      });
    }
  };
